import React, { useEffect, useRef, useMemo } from 'react';
import '../../css/chats/MentionsDropdown.css';

/**
 * Дропдаун для @mentions — список участников чата с фильтром.
 * Поддерживает клавиатурную навигацию: ↑/↓ — выбор, Enter — вставка, Esc — закрытие.
 *
 * @param {Array<{id: string, displayName: string, avatarUrl?: string}>} participants
 * @param {string} query — текст после @ для фильтрации
 * @param {number} selectedIndex — индекс выбранного элемента
 * @param {Function} onSelect — callback(profile)
 * @param {Function} onClose — callback() при Esc
 */
export default function MentionsDropdown({ participants, query, selectedIndex, onSelect, onClose, onHover }) {
    const listRef = useRef(null);

    // Фильтрация участников по запросу
    const filtered = useMemo(() => {
        if (!query) return participants.slice(0, 8);
        const q = query.toLowerCase();
        return participants
            .filter(p => p.displayName?.toLowerCase().includes(q))
            .slice(0, 8);
    }, [participants, query]);

    // Скролл к выбранному элементу при клавиатурной навигации
    useEffect(() => {
        const el = listRef.current?.children[selectedIndex];
        if (el) {
            el.scrollIntoView({ block: 'nearest' });
        }
    }, [selectedIndex]);

    // Esc — закрытие (обрабатывается в SendMessageForm, но дублируем для надёжности)
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    if (filtered.length === 0) return null;

    return (
        <div className="mentions-dropdown" ref={listRef}>
            {filtered.map((profile, index) => (
                <button
                    key={profile.id}
                    type="button"
                    className={`mentions-dropdown__item ${index === selectedIndex ? 'mentions-dropdown__item--active' : ''}`}
                    onMouseDown={(e) => {
                        e.preventDefault(); // не уводим фокус из textarea
                        onSelect(profile);
                    }}
                    onMouseEnter={() => onHover?.(index)}
                >
                    <span className="mentions-dropdown__avatar">
                        {profile.avatarUrl ? (
                            <img src={profile.avatarUrl} alt="" className="mentions-dropdown__avatar-img" />
                        ) : (
                            <span className="mentions-dropdown__avatar-placeholder">
                                {profile.displayName?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                        )}
                    </span>
                    <span className="mentions-dropdown__name">{profile.displayName}</span>
                </button>
            ))}
        </div>
    );
}
